// AIHandoffAlert.jsx
// Global popup + chime that fires when the AI receptionist can't handle a
// client message and flags it for a human. Pairs with ai_handoff_alerts.
//
// Triggers (set by the bot):
//   • Unknown question (insurance, custom pricing, treatments not in menu)
//   • Treatment complaint / pain report
//   • No-show client tried to book — bot routed them to the office
//   • Bot couldn't resolve / hostile client
//
// Visible to: front_desk, director, admin, owner, manager.
//
// Difference from NewLeadAlert: shows the actual SMS that stumped the bot and
// the reason summary, plus a 🚨🆘 vibe to make it impossible to miss.

import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";

const C = {
  surface: "#ffffff",
  border: "#e8e2d8",
  red: "#b54040",
  redL: "rgba(181,64,64,0.10)",
  orange: "#e87d1f",
  text: "#1a1a2e",
  muted: "#6b6356",
  light: "#f4f0ea",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const ALERT_ROLES = new Set(["front_desk", "director", "admin", "owner", "manager"]);

function formatPhone(p) {
  if (!p) return "";
  const d = p.replace(/\D/g, "");
  if (d.length === 10) return "(" + d.slice(0, 3) + ") " + d.slice(3, 6) + "-" + d.slice(6);
  if (d.length === 11 && d.startsWith("1")) return "+1 (" + d.slice(1, 4) + ") " + d.slice(4, 7) + "-" + d.slice(7);
  return p;
}

// Reuse the same shared AudioContext + unlock pattern as NewLeadAlert so the
// browser doesn't gate the chime behind autoplay rules.
let _sharedAudioCtx = null;
function getAudioCtx() {
  if (!_sharedAudioCtx) {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) _sharedAudioCtx = new Ctx();
    } catch (e) { /* ignore */ }
  }
  return _sharedAudioCtx;
}
if (typeof window !== "undefined" && !window.__elyonAudioUnlockBound) {
  window.__elyonAudioUnlockBound = true;
  const unlock = () => {
    const ctx = getAudioCtx();
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
  };
  ["click", "keydown", "touchstart", "pointerdown"].forEach(ev =>
    window.addEventListener(ev, unlock, { once: false, passive: true })
  );
}

// Urgent-sounding two-tone alert: high-low chirp repeated 3x. Distinct from
// the new-lead arpeggio so staff can hear-recognize WHICH alert just fired.
function playAlertChime() {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const base = ctx.currentTime + 0.05;
    const pattern = [1200, 800];
    for (let rep = 0; rep < 3; rep++) {
      pattern.forEach((freq, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "square";  // edgier than sine — sounds like an alarm
        o.frequency.value = freq;
        o.connect(g); g.connect(ctx.destination);
        const t0 = base + rep * 0.9 + i * 0.18;
        g.gain.setValueAtTime(0, t0);
        g.gain.linearRampToValueAtTime(0.25, t0 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.22);
        o.start(t0);
        o.stop(t0 + 0.25);
      });
    }
  } catch (e) { console.warn("AI handoff chime failed:", e); }
}

const REASON_LABEL = {
  unknown_question: "Unknown question",
  treatment_complaint: "🩹 Treatment complaint",
  no_show_blocked: "No-show — bot routed to office",
  cant_resolve: "Couldn't resolve",
  manipulation: "Hostile / abuse",
  // Sophia booked a visit but takes no card — the desk calls to secure it.
  secure_the_spot: "💳 Take card to secure the spot",
};

// A secure-the-spot alert is a task, not an emergency: calm gold styling and
// its own wording, so staff don't read it as "the bot is stuck".
const isSecureSpot = (a) => a?.reason_type === "secure_the_spot";

export default function AIHandoffAlert({ user, profile }) {
  const [queue, setQueue] = useState([]); // [{ alert, arrivedAt }]
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [now, setNow] = useState(Date.now());
  const seenIdsRef = useRef(new Set());
  const role = (profile?.role || "").toLowerCase();
  // ★ Respect the user's notif_prefs.complaints toggle. AI handoff alerts
  // fire on complaints / unknown questions / bot escalations — same bucket
  // as the "Complaint / Issue" preference in User Settings.
  const notifEnabled = profile?.notif_prefs?.complaints !== false;
  const isAllowed = ALERT_ROLES.has(role) && notifEnabled;

  useEffect(() => {
    if (queue.length === 0) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [queue.length]);

  useEffect(() => {
    if (!isAllowed) return;

    const surface = (alert, arrivedAt) => {
      if (!alert || !alert.id) return;
      if (seenIdsRef.current.has(alert.id)) return;
      seenIdsRef.current.add(alert.id);
      setQueue(q => [...q, { alert, arrivedAt }]);
      playAlertChime();
    };

    // On mount, catch any handoffs that are still pending — if the staff
    // refreshes the page, they shouldn't lose visibility on open alerts.
    (async () => {
      const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(); // last 24h
      const { data } = await supabase
        .from("ai_handoff_alerts")
        .select("*")
        .eq("status", "pending")
        .gte("created_at", cutoff)
        .order("priority", { ascending: true })
        .order("created_at", { ascending: false })
        .limit(10);
      (data || []).forEach(a => {
        const arrived = a.created_at ? new Date(a.created_at).getTime() : Date.now();
        surface(a, arrived);
      });
    })();

    const ch = supabase.channel("ai-handoff-alert-" + Math.random().toString(36).slice(2))
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "ai_handoff_alerts" }, (payload) => {
        const a = payload.new;
        if (!a || a.status !== "pending") return;
        const arrived = a.created_at ? new Date(a.created_at).getTime() : Date.now();
        surface(a, arrived);
      })
      .subscribe();

    return () => { supabase.removeChannel(ch); };
  }, [isAllowed]);

  if (!isAllowed || queue.length === 0) return null;

  const current = queue[0];
  const alert = current.alert;
  const elapsedSec = Math.max(0, Math.floor((now - current.arrivedAt) / 1000));
  const isHigh = alert.priority === 1;

  const acknowledge = async (action) => {
    try {
      await supabase.from("ai_handoff_alerts").update({
        status: action === "resolved" ? "resolved" : "acknowledged",
        acknowledged_at: new Date().toISOString(),
        acknowledged_by_user_id: user?.id || null,
        acknowledged_by_name: profile?.full_name || profile?.email || "Staff",
        acknowledged_seconds: elapsedSec,
        ...(action === "resolved" ? {
          resolved_at: new Date().toISOString(),
          resolved_by_name: profile?.full_name || profile?.email || "Staff",
        } : {}),
      }).eq("id", alert.id);
    } catch (e) { console.warn("AI handoff ack failed:", e); }
    setQueue(q => q.slice(1));
  };

  const snooze = () => {
    setQueue(q => {
      const [head, ...rest] = q;
      return [...rest, head];
    });
  };

  // Secure-the-spot alerts can also be closed by texting the client the card
  // link — useful when she doesn't pick up.
  const sendCardLink = async () => {
    if (!alert.phone || !alert.client_id) return;
    setSendingLink(true);
    const first = (alert.client_name || "there").trim().split(/\s+/)[0];
    const link = `https://www.elyonhq.com/card?cid=${alert.client_id}&n=${encodeURIComponent(first)}`;
    const msg = `Hi ${first}, it's Nova Med Spa 💛 To hold your appointment, please save a card on file — you won't be charged today. Free cancellation up to 24 hours before your visit; changes within 24h are $25 (credited to your treatment); no-shows $50. Secure link: ${link}`;
    try {
      const { data, error } = await supabase.functions.invoke("send-sms", {
        body: { to: alert.phone, message: msg, sender: "system", source: "card_guarantee" },
      });
      if (error || data?.ok === false) { alert2("SMS failed: " + (error?.message || data?.error || "unknown")); }
      else setLinkSent(true);
    } catch (e) { alert2("SMS failed: " + (e?.message || e)); }
    setSendingLink(false);
  };
  const alert2 = (m) => { try { window.alert(m); } catch (_) {} };

  const tel = (alert.phone || "").replace(/[^\d+]/g, "");
  const elapsedDisplay = elapsedSec < 60
    ? `${elapsedSec}s`
    : `${Math.floor(elapsedSec / 60)}m ${elapsedSec % 60}s`;
  const secureSpot = isSecureSpot(alert);
  const accent = secureSpot ? "#b48c3c" : isHigh ? C.red : C.orange;
  const reasonLabel = REASON_LABEL[alert.reason_type] || "Help needed";

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(15,15,25,0.78)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, animation: "elyonFadeIn 0.18s ease-out",
    }}>
      <div style={{
        background: C.surface, borderRadius: 18, maxWidth: 540, width: "100%",
        boxShadow: "0 24px 70px rgba(0,0,0,0.45)", overflow: "hidden",
        border: `3px solid ${accent}`,
      }}>
        <div style={{
          padding: "16px 22px", background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
          color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 26, lineHeight: 1 }}>{secureSpot ? "💳📞" : "🚨🆘"}</span>
            <div>
              <div style={{ fontFamily: S, fontSize: 18, fontWeight: 800, letterSpacing: "0.02em" }}>
                {secureSpot ? "Call to Secure the Spot" : "AI Bot Needs Help"}
              </div>
              <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>
                {secureSpot ? "Sophia booked this visit — take card details to hold it" : reasonLabel}
              </div>
            </div>
          </div>
          <div style={{
            background: "rgba(255,255,255,0.18)", padding: "6px 12px", borderRadius: 99,
            fontSize: 12, fontWeight: 800, letterSpacing: "0.06em",
          }}>{elapsedDisplay}</div>
        </div>

        <div style={{ padding: "20px 22px" }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>Client</div>
          <div style={{ fontSize: 19, fontWeight: 800, color: C.text, fontFamily: S, marginBottom: 2 }}>
            {alert.client_name || "Unknown name"}
          </div>
          <div style={{ fontSize: 14, color: C.muted, fontWeight: 600 }}>
            {formatPhone(alert.phone)}
          </div>

          <div style={{ marginTop: 16, padding: "12px 14px", background: secureSpot ? "rgba(180,140,60,0.10)" : C.redL, borderLeft: `3px solid ${accent}`, borderRadius: 8 }}>
            <div style={{ fontSize: 10, color: accent, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>{secureSpot ? "What to do" : "What stumped the bot"}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 }}>{alert.reason_summary}</div>
            {alert.client_message && (
              <div style={{ fontSize: 12.5, color: C.text, fontStyle: "italic", lineHeight: 1.45 }}>
                "{alert.client_message}"
              </div>
            )}
          </div>

          {alert.bot_response_sent && (
            <div style={{ marginTop: 10, padding: "8px 12px", background: C.light, borderRadius: 8, fontSize: 11, color: C.muted }}>
              <span style={{ fontWeight: 700 }}>Bot's last reply:</span> {alert.bot_response_sent}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 22, flexWrap: "wrap" }}>
            <a href={`tel:${tel}`} onClick={() => acknowledge("called")} style={{
              flex: "1 1 160px", textAlign: "center", padding: "12px 16px", background: accent,
              color: "#fff", borderRadius: 10, textDecoration: "none", fontSize: 14, fontWeight: 800,
              fontFamily: F, letterSpacing: "0.04em",
            }}>📞 {secureSpot ? "Call for card" : "Call Client"}</a>
            {secureSpot && (
              <button onClick={sendCardLink} disabled={sendingLink} style={{
                flex: "1 1 150px", padding: "12px 16px", background: "rgba(180,140,60,0.12)",
                color: "#8a6f2e", border: "1px solid rgba(180,140,60,0.45)", borderRadius: 10,
                fontSize: 13, fontWeight: 700, cursor: sendingLink ? "wait" : "pointer", fontFamily: F,
              }}>{sendingLink ? "Sending…" : linkSent ? "✓ Link sent" : "📲 Text card link"}</button>
            )}
            <button onClick={() => acknowledge("acknowledged")} style={{
              flex: "1 1 130px", padding: "12px 16px", background: "#fff", color: C.text,
              border: `1px solid ${C.border}`, borderRadius: 10, fontSize: 13, fontWeight: 700,
              cursor: "pointer", fontFamily: F,
            }}>✅ Got it</button>
            <button onClick={snooze} style={{
              padding: "12px 16px", background: "transparent", color: C.muted,
              border: `1px solid ${C.border}`, borderRadius: 10, fontSize: 12, fontWeight: 600,
              cursor: "pointer", fontFamily: F,
            }}>Snooze</button>
          </div>
        </div>

        {queue.length > 1 && (
          <div style={{ padding: "8px 22px", borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.muted, textAlign: "center", fontWeight: 600 }}>
            +{queue.length - 1} more handoff{queue.length - 1 === 1 ? "" : "s"} waiting
          </div>
        )}
      </div>

      <style>{`@keyframes elyonFadeIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }`}</style>
    </div>
  );
}
